import {
  AnalyticsPermissions,
  ImsPermissions,
} from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import CoinsStacked03Icon from "@redotech/redo-icons/coins-stacked-03.svg";
import CoinsStacked03FilledIcon from "@redotech/redo-icons/coins-stacked-03_filled.svg";
import CurrencyDollarCircleIcon from "@redotech/redo-icons/currency-dollar-circle.svg";
import CurrencyDollarCircleFilledIcon from "@redotech/redo-icons/currency-dollar-circle_filled.svg";
import FileCheck02Icon from "@redotech/redo-icons/file-check-02.svg";
import FileCheck02FilledIcon from "@redotech/redo-icons/file-check-02_filled.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { useCostLedgerStatus } from "@redotech/redo-merchant-app-common/hooks/use-cost-ledger-status";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { useCapability } from "@redotech/redo-merchant-app-common/use-capability";
import { useTeamCanCycleCount } from "@redotech/redo-merchant-inventory-app/cycle-counts/cycle-counts-available";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import BarChart10FilledIcon from "@redotech/redo-web/icon-old/bar-chart-10-filled.svg";
import BarChart10Icon from "@redotech/redo-web/icon-old/bar-chart-10.svg";
import { memo } from "react";
import { useMatch } from "react-router-dom";

export const InventoryAnalyticsItem = memo(function InventoryAnalyticsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  const imsEnabled = useCapability("ims");

  const canAccessInventory = useHasPermission(ImsPermissions.ACCESS_INVENTORY);
  const canViewReports = useHasPermission(ImsPermissions.VIEW_REPORTS);
  const canViewCustomAnalytics = useHasPermission(
    AnalyticsPermissions.VIEW_ANALYTICS,
  );
  const canAccessCycleCounts = useHasPermission(
    ImsPermissions.ACCESS_CYCLE_COUNTS,
  );

  const teamCanCycleCount = useTeamCanCycleCount();

  const showCycleCounts =
    imsEnabled && canAccessCycleCounts && teamCanCycleCount;
  const showAdjustmentReports = imsEnabled && canAccessInventory;
  const costLedger = useCostLedgerStatus({ enabled: showAdjustmentReports });
  const showCostReports =
    showAdjustmentReports &&
    !!costLedger.value &&
    costLedger.value.setup !== "needsSourceOfTruth";
  const showCustomReports =
    imsEnabled && canViewReports && canViewCustomAnalytics;
  const showAnalytics =
    showCycleCounts || showAdjustmentReports || showCustomReports;

  const analyticsRoute = `${routes.inventoryManagementBaseUrl}/analytics`;
  const analyticsChildSelected = useMatch(`${analyticsRoute}/*`);
  const adjustmentReportSelected = useMatch(
    `${routes.inventoryManagementBaseUrl}/adjustment-report`,
  );
  const anyChildSelected =
    !!analyticsChildSelected || !!adjustmentReportSelected;

  if (!showAnalytics) {
    return null;
  }

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={anyChildSelected}
      FilledIcon={BarChart10FilledIcon}
      friendlyName={t("Analytics", {
        context: "inventory and purchasing navigation label",
      })}
      id="inventory-analytics"
      isFolder
      UnfilledIcon={BarChart10Icon}
    >
      <AdjustmentReportsItem hidden={!showAdjustmentReports} />
      <CostOfGoodsSoldItem hidden={!showCostReports} />
      <InventoryValuationItem hidden={!showCostReports} />
      <CustomAnalyticsItem hidden={!showCustomReports} />
      <CycleCountsAnalyticsItem hidden={!showCycleCounts} />
    </RedoSecondaryNavItem>
  );
});

const AdjustmentReportsItem = memo(function AdjustmentReportsItem({
  hidden,
}: {
  hidden: boolean;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/adjustment-report`,
      }}
      FilledIcon={FileCheck02FilledIcon}
      friendlyName={t("Adjustment reports", {
        context: "inventory and purchasing navigation label",
      })}
      hidden={hidden}
      id="inventory-adjustment-reports"
      UnfilledIcon={FileCheck02Icon}
    />
  );
});

const CostOfGoodsSoldItem = memo(function CostOfGoodsSoldItem({
  hidden,
}: {
  hidden: boolean;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/analytics/cost-of-goods-sold`,
      }}
      FilledIcon={CurrencyDollarCircleFilledIcon}
      friendlyName={t("Cost of goods sold", {
        context: "inventory and purchasing navigation label",
      })}
      hidden={hidden}
      id="inventory-analytics-cost-of-goods-sold"
      UnfilledIcon={CurrencyDollarCircleIcon}
    />
  );
});

const InventoryValuationItem = memo(function InventoryValuationItem({
  hidden,
}: {
  hidden: boolean;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/analytics/inventory-valuation`,
      }}
      FilledIcon={CoinsStacked03FilledIcon}
      friendlyName={t("Inventory valuation", {
        context: "inventory and purchasing navigation label",
      })}
      hidden={hidden}
      id="inventory-analytics-inventory-valuation"
      UnfilledIcon={CoinsStacked03Icon}
    />
  );
});

const CustomAnalyticsItem = memo(function CustomAnalyticsItem({
  hidden,
}: {
  hidden: boolean;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.inventoryAnalyticsReportsHref }}
      beta
      friendlyName={t("Custom analytics", {
        context: "inventory and purchasing navigation label",
      })}
      hidden={hidden}
      id="inventory-analytics-custom-reports"
    />
  );
});

const CycleCountsAnalyticsItem = memo(function CycleCountsAnalyticsItem({
  hidden,
}: {
  hidden: boolean;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/analytics/cycle-counts`,
      }}
      friendlyName={t("Cycle counts", {
        context: "inventory and purchasing navigation label",
      })}
      hidden={hidden}
      id="inventory-analytics-cycle-counts"
    />
  );
});
